/* eslint-disable no-magic-numbers */
/* eslint-disable max-lines */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { OneArmedBanditConfiguration } from '@luckycart/xpn-types';

import {
  InnerStripe,
  LeftNeedleContainer,
  OneArmedBanditBackground,
  OneArmedBanditContainer,
  OneArmedBanditSpinner,
  OuterStripe,
  RightNeedleContainer,
  SlotImage,
  TopSpinnerShadow,
  BottomSpinnerShadow,
  SlotsContainer,
} from './OneArmedBanditPlugin.style';
import { ReactComponent as NeedleSVG } from './images/red-needle.svg';
import { ReactComponent as SpinnerBackgroundSVG } from './images/spinner-background.svg';

import { BlockInfoType } from '../../common-types/BlockInfo.type';

const COL_HEIGHT = 46.5;
const FPS = 16;
const INITIAL_SPEED = 10;

interface SlotInfo { alwaysDisplayWinIcon: boolean, deceleration: number }
const SLOTS_INFO: SlotInfo[] = [
  { alwaysDisplayWinIcon: true, deceleration: 0.3 },
  { alwaysDisplayWinIcon: true, deceleration: 0.2 },
  { alwaysDisplayWinIcon: false, deceleration: 0.1 },
];

function playingAnimationInterval (
  animationValue: React.RefObject<{ speed: number, top: number }>,
  ref: React.RefObject<HTMLDivElement>
): void {
  if (animationValue.current !== null) {
    const newTop = (animationValue.current.top - animationValue.current.speed) % COL_HEIGHT;
    if (ref.current !== null) {
      ref.current.style.transform = `translateY(${newTop}vh)`;
    }
    animationValue.current.top = newTop;
  }
}

function handleSlotColumnStoppingAnimation (
  top: number,
  speed: number,
  hasWon: boolean,
  slotInfo: SlotInfo
): { speed: number, top: number } {
  if (speed <= 1) {
    if ((hasWon || slotInfo.alwaysDisplayWinIcon) && top >= -1 && top <= 0) {
      return { speed: 0, top: 0 };
    }
    if (!hasWon && !slotInfo.alwaysDisplayWinIcon && top >= -16 && top <= -14) {
      return { speed: 0, top: -15 };
    }
    return { speed: 1, top: (top - speed) % COL_HEIGHT };
  }
  const newSpeed = speed - slotInfo.deceleration;
  return {
    speed: newSpeed >= 0.01
      ? newSpeed
      : 0.1,
    top: (top - speed) % COL_HEIGHT,
  };
}

// eslint-disable-next-line max-params
function stopAnimationInterval (
  animationValue: React.RefObject<{ speed: number, top: number }>,
  slotInfo: SlotInfo,
  hasWon: boolean,
  ref: React.RefObject<HTMLDivElement>,
  intervalRef: React.RefObject<NodeJS.Timeout | undefined>,
  setStoppingRolls: React.Dispatch<React.SetStateAction<boolean[]>>,
  rollPosition: number
): void {
  if (animationValue.current !== null) {
    const { top, speed } = handleSlotColumnStoppingAnimation(animationValue.current.top, animationValue.current.speed, hasWon, slotInfo);
    animationValue.current.top = top;
    animationValue.current.speed = speed;
    if (ref.current !== null) {
      ref.current.style.transform = `translateY(${top}vh)`;
    }
    if (speed < 0.001 && intervalRef.current !== null && intervalRef.current !== undefined) {
      clearInterval(intervalRef.current);
      setStoppingRolls((prevState) => {
        return [
          ...prevState.slice(0, rollPosition),
          true,
          ...prevState.slice(rollPosition + 1),
        ];
      });
    }
  }
}

interface Props {
  config: OneArmedBanditConfiguration;
  onPluginDone?: () => void;
  hasWon: null | boolean;
  addEditableBlock?: (block: BlockInfoType) => void;
  removeEditableBlock?: (configPath: string) => void;
  onBlockEdit?: React.MouseEventHandler<HTMLDivElement>;
  isPlaying: boolean;
  isEdition: boolean;
  onLoad?: () => void;
}

export const OneArmedBanditPlugin: React.FunctionComponent<Props> = ({
  config,
  addEditableBlock,
  removeEditableBlock,
  isEdition,
  onBlockEdit,
  hasWon,
  onPluginDone,
  isPlaying,
  onLoad,
}: Props) => {
  const leftSlotAnimationValue = useRef<{ speed: number, top: number }>({ speed: INITIAL_SPEED, top: 0 });
  const centerSlotAnimationValue = useRef<{ speed: number, top: number }>({ speed: INITIAL_SPEED, top: 0 });
  const rightSlotAnimationValue = useRef<{ speed: number, top: number }>({ speed: INITIAL_SPEED, top: 0 });
  const leftRef = useRef<HTMLDivElement>(null);
  const centerRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const leftInterval = useRef<NodeJS.Timeout | null>(null);
  const centerInterval = useRef<NodeJS.Timeout | null>(null);
  const rightInterval = useRef<NodeJS.Timeout | null>(null);
  const slotsInfo = useMemo(() => SLOTS_INFO.sort(() => Math.random() - 0.5), []);
  const [firstPartTimelastEnd, setFirstPartTimelastEnd] = useState(false);
  const [stoppedRolls, setStoppedRolls] = useState([false, false, false]);

  const images = useMemo(() => [
    {
      url: config.otherImg.imageUrl,
      key: 'other_one',
    },
    {
      url: config.winImg.imageUrl,
      key: 'win_one',
    },
    {
      url: config.loseImg.imageUrl,
      key: 'lose_one',
    },
    {
      url: config.otherImg.imageUrl,
      key: 'other_two',
    },
    {
      url: config.winImg.imageUrl,
      key: 'win_two',
    },
    {
      url: config.loseImg.imageUrl,
      key: 'lose_two',
    },
  ], [config.loseImg.imageUrl, config.winImg.imageUrl, config.otherImg.imageUrl]);

  const dataTemplateType = 'plugin';
  const dataTemplateConfig = 'screens.game.plugin';
  const displayName = 'plugin';

  useEffect(() => {
    if (isPlaying) {
      leftInterval.current = setInterval(() => {
        playingAnimationInterval(leftSlotAnimationValue, leftRef);
      }, FPS);
      centerInterval.current = setInterval(() => {
        playingAnimationInterval(centerSlotAnimationValue, centerRef);
      }, FPS);
      rightInterval.current = setInterval(() => {
        playingAnimationInterval(rightSlotAnimationValue, rightRef);
      }, FPS);
      setTimeout(() => setFirstPartTimelastEnd(true), 3000);
    }
  }, [isPlaying]);

  useEffect(() => {
    if (isPlaying && hasWon !== null && firstPartTimelastEnd) {
      if (leftInterval.current !== null) {
        clearInterval(leftInterval.current);
      }
      if (centerInterval.current !== null) {
        clearInterval(centerInterval.current);
      }
      if (rightInterval.current !== null) {
        clearInterval(rightInterval.current);
      }
      leftInterval.current = setInterval(() => {
        stopAnimationInterval(leftSlotAnimationValue, slotsInfo[0], hasWon, leftRef, leftInterval, setStoppedRolls, 0);
      }, FPS);
      centerInterval.current = setInterval(() => {
        stopAnimationInterval(centerSlotAnimationValue, slotsInfo[1], hasWon, centerRef, centerInterval, setStoppedRolls, 1);
      }, FPS);
      rightInterval.current = setInterval(() => {
        stopAnimationInterval(rightSlotAnimationValue, slotsInfo[2], hasWon, rightRef, rightInterval, setStoppedRolls, 2);
      }, FPS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasWon, firstPartTimelastEnd]);

  useEffect(() => {
    if (stoppedRolls.every((stoppedRoll) => stoppedRoll)) {
      setTimeout(() => {
        if (onPluginDone !== undefined) {
          onPluginDone();
        }
      }, 4000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stoppedRolls]);

  return (
    <OneArmedBanditContainer
      onBlockEdit={onBlockEdit}
      dataTemplateType={dataTemplateType}
      displayName={displayName}
      dataTemplateConfig={dataTemplateConfig}
      isEdition={isEdition}
      addEditableBlock={addEditableBlock}
      removeEditableBlock={removeEditableBlock}
      onLoad={onLoad}
      neededAssets={[config.winImg.imageUrl, config.loseImg.imageUrl, config.otherImg.imageUrl]}
    >
      <OuterStripe>
        <InnerStripe>
          <OneArmedBanditBackground color={config.background.color}>
            <OneArmedBanditSpinner>
              <TopSpinnerShadow />
              <BottomSpinnerShadow />
              <SlotsContainer ref={leftRef}>
                {
                  images.map(({ url, key }) => <SlotImage isPlaying={isPlaying} speed={leftSlotAnimationValue.current.speed} key={key} src={url === null
                    ? undefined
                    : url as string | undefined} />)
                }
              </SlotsContainer>
              <SpinnerBackgroundSVG />
            </OneArmedBanditSpinner>
            <OneArmedBanditSpinner>
              <TopSpinnerShadow />
              <BottomSpinnerShadow />
              <SlotsContainer ref={centerRef}>
                {
                  images.map(({ url, key }) => <SlotImage isPlaying={isPlaying} speed={centerSlotAnimationValue.current.speed} key={key} src={url === null
                    ? undefined
                    : url as string | undefined} />)
                }
              </SlotsContainer>
              <SpinnerBackgroundSVG />
            </OneArmedBanditSpinner>
            <OneArmedBanditSpinner>
              <TopSpinnerShadow />
              <BottomSpinnerShadow />
              <SlotsContainer ref={rightRef}>
                {
                  images.map(({ url, key }) => <SlotImage isPlaying={isPlaying} speed={rightSlotAnimationValue.current.speed} key={key} src={url === null
                    ? undefined
                    : url as string | undefined} />)
                }
              </SlotsContainer>
              <SpinnerBackgroundSVG />
            </OneArmedBanditSpinner>
          </OneArmedBanditBackground>
        </InnerStripe>
      </OuterStripe>
      <LeftNeedleContainer>
        <NeedleSVG />
      </LeftNeedleContainer>
      <RightNeedleContainer>
        <NeedleSVG />
      </RightNeedleContainer>
    </OneArmedBanditContainer>
  );
};
